import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getDrinkRequestDetails } from '../../../../../services/Admin/NamedDrinksRequestApi';

const AiDrinkRequestDetailsDialog = ({ open, onClose, requestId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && requestId) {
      fetchDetails();
    }
  }, [open, requestId]);

  const fetchDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getDrinkRequestDetails(requestId);
      setData(response?.data);
    } catch (err) {
      console.error('خطأ في جلب التفاصيل:', err);
      setError(err.message || 'حدث خطأ في جلب التفاصيل');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: 'warning' },
      approved: { label: 'موافق عليه', color: 'success' },
      rejected: { label: 'مرفوض', color: 'error' },
    };
    const statusInfo = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const parseIngredients = (ingredientsString) => {
    if (!ingredientsString) return [];
    try {
      // إذا كانت JSON string
      if (typeof ingredientsString === 'string') {
        return JSON.parse(ingredientsString);
      }
      return Array.isArray(ingredientsString) ? ingredientsString : [];
    } catch (e) {
      console.error('خطأ في تحليل المكونات:', e);
      return [];
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl">
      <DialogTitle sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          تفاصيل طلب المشروب
        </Typography>
        <Button onClick={onClose} size="small">
          <CloseIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <Box>
            {/* معلومات الطلب الأساسية */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                📋 معلومات الطلب الأساسية
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <strong>رقم الطلب:</strong> #{data.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <strong>الحالة:</strong> {getStatusChip(data.status)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <strong>تاريخ الإنشاء:</strong> {formatDate(data.created_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    <strong>آخر تحديث:</strong> {formatDate(data.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* معلومات الطلب المقترح */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                🍹 معلومات المشروب المقترح
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>اسم المشروب (عربي):</strong> {data.drink_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>اسم المشروب (إنجليزي):</strong> {data.drink_name_en}
                </Typography>
              </Box>
              {data.notes && (
                <Box sx={{ p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>الملاحظات:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {data.notes}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* تفاصيل المشروب المرتبط */}
            {data.drink && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    ☕ تفاصيل المشروب
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>الاسم:</strong> {data.drink.drink_name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>السعر:</strong> {data.drink.price} ريال
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>درجة الحرارة:</strong> {data.drink.temperature || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>وقت التحضير:</strong> {data.drink.prepare_time} دقيقة
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>الوصف:</strong> {data.drink.description || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>الحالة:</strong> {data.drink.status === 1 ? '✅ نشط' : '❌ غير نشط'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* المكونات */}
                  {data.drink.ingredients && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                        📦 المكونات
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead sx={{ backgroundColor: '#e8f5e9' }}>
                            <TableRow>
                              <TableCell align="right">المكون</TableCell>
                              <TableCell align="right">الكمية</TableCell>
                              <TableCell align="right">الوحدة</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {parseIngredients(data.drink.ingredients).map((ingredient, index) => (
                              <TableRow key={index}>
                                <TableCell align="right">{ingredient.name}</TableCell>
                                <TableCell align="right">{ingredient.amount}</TableCell>
                                <TableCell align="right">{ingredient.unit}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              </>
            )}

            {/* معلومات المستخدم */}
            {data.user && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    👤 معلومات المستخدم
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>الاسم:</strong> {data.user.name}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>البريد الإلكتروني:</strong> {data.user.email}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>رقم الهاتف:</strong> {data.user.phone || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>الهاتف البديل:</strong> {data.user.phone2 || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>العنوان:</strong> {data.user.address2 || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        <strong>النوع:</strong> {data.user.type || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        ) : (
          <Alert severity="info">لا توجد بيانات</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          إغلاق
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiDrinkRequestDetailsDialog;
